/**
 * Update Blog Feature
 * 100% Isolated: Update blog functionality that can be changed independently
 */
import { useState, useCallback } from 'react';
import { blogApiService } from '../api';
import { userService } from '../../user/service';
import type { UpdateBlogRequest, BlogPost } from '../api';

export const useUpdateBlog = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateBlog = useCallback(async (blogId: string, blogData: Omit<UpdateBlogRequest, 'blog_id'>): Promise<BlogPost | null> => {
    setLoading(true);
    setError(null);

    try {
      // Check authentication
      if (!userService.isAuthenticated()) {
        throw new Error('Authentication required to update a blog');
      }

      // Update blog via API - now expects blog_id in the request body
      const updateResponse = await blogApiService.updateBlog({ blog_id: blogId, ...blogData });
      
      if (!updateResponse.success || !updateResponse.data) {
        throw new Error(updateResponse.error?.message || 'Failed to update blog post');
      }
      
      // Fetch the updated blog details
      const response = await blogApiService.getBlogById(blogId);
      
      if (!response.success || !response.data) {
        throw new Error('Failed to fetch updated blog post');
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update blog';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateBlog,
    loading,
    error,
    clearError: () => setError(null)
  };
};
